// « Studio », the page of one of our ads (#/studio/item/<pub id>): everything about its making on
// one short page, nothing to open elsewhere — on top, our ad (its video on the left, its state on
// the right: what it waits for, progress, what blocks it) and, beside it, « Vient de » (a small
// thumbnail of the competitor's ad, with its title and summary) or, for an ad on brief, the
// brief's card (its status, where the script stands, « Valider le script » / « Demander des
// retouches »); below, folded sections that open on a click: « Brief » (open while the script is
// not approved: its fields, « Modifier », its reference files, its history), « Découpe du
// concurrent » (one capture per scene), « Nos séquences » (one box per sequence: its start image,
// then its video) and « Informations ». Every element has the same small tools: description
// (paragraph icon), history, « Regénérer » and the full sheet, in small dialogs (the brief: no
// « Regénérer », its rework goes through « Demander des retouches »). Data: GET
// /api/studio/items/:id/dossier (server/studio/service.js dossier()). Text written by agents is
// always shown as text.
import { h, button, iconButton, icon, select, emptyState, spinner, classNames } from '../dom.js';
import { t } from '../i18n/fr.js';
import { relativeTime, formatDateTime, deptColor } from '../format.js';
import { floorNumberOf, studioHash, routeHash } from '../store.js';
import { studioDept, typeOf, statusInfo, refLabel, fileRoles } from './studio/model.js';
import {
  pubVideos, latestFinal, mediaFiles, waitingReason, pubProgress, captureOf, blockers, describeFields, sequenceBoxes,
  pubSequenceIds, usedInPub, briefPhase, briefActions, briefEditable, sceneGap, sceneGaps, isForced, goesInPub,
} from './studio/pub-model.js';
import { fieldRows as itemFieldRows, editForm, filesPanel, historyList, liveReloadWaits } from './studio/item-parts.js';
import { copyText } from './tasks/components.js';
import './studio/strings.js';

const RELOAD_DEBOUNCE_MS = 500;

/**
 * The short name of an ad for the way back: « Pub 9 » of « Pub 9 - Création Ads · Les lunettes… »
 * or « Pub 9 · … » (else the start of its title).
 */
export function shortTitle(title) {
  const s = String(title ?? '').trim();
  const m = /^((?:pub|remix)\s+\d+)(?=$|\s+-\s)/i.exec(s.split(' · ')[0]);
  if (m) return m[1];
  return s.length > 32 ? `${s.slice(0, 31).trimEnd()}…` : s;
}
const enc = encodeURIComponent;
/** Folded sections remembered per browser (« Découpe », « Nos séquences », « Informations »). */
const FOLD_KEY = 'maeck.studio.pub.open';

function readOpen() {
  try { return new Set(JSON.parse(globalThis.localStorage?.getItem(FOLD_KEY) || '[]')); } catch { return new Set(); }
}

function writeOpen(set) {
  try { globalThis.localStorage?.setItem(FOLD_KEY, JSON.stringify([...set])); } catch { /* private mode, blocked storage */ }
}

/**
 * The page of one of our ads.
 * @param {object} ctx app context
 * @param {{id: string}} route
 * @param {object} layer the Studio sheet
 */
export function createPubView(ctx, route, layer) {
  const { store } = ctx;
  const timers = new Set();
  let destroyed = false;
  let seq = 0;
  let dossier = null;
  /** Ids shown on the page: a live event about one of them reloads it. */
  let ids = new Set();
  const open = readOpen();
  /** The element shown inside the page (#/studio/item/<pub>/v/<element>), else null: the ad itself. */
  let sub = route.tab === 'v' && route.sub ? route.sub : null;
  /** The versions of the element shown (GET …/takes), by element id; absent while loading. */
  const takesOf = new Map();
  /** Where the ad's page was scrolled to before an element was opened (restored on « ← Pub »). */
  let pubScroll = 0;
  /** An element of another ad or service, read on its own when it is not in this ad's dossier. */
  let loose = null;
  /**
   * The brief's fields shown as a form (« Modifier »): the form survives renders; while it is on
   * screen, live reloads wait (`missed`) until it is saved or cancelled.
   */
  let editing = false;
  let editNode = null;
  let missed = false;
  /** The « Brief » section opened or closed by hand, for the brief's status it was done at. */
  let briefFold = null;
  const el = h('article', { class: 'st-view sp' }, h('div', { class: 'st-loading' }, spinner()));

  const deptById = (id) => store.get().office.departments.find((d) => d.id === id) || null;
  const agentById = (id) => store.get().office.agents.find((a) => a.id === id) || null;
  const floors = () => store.get().office.floors;
  const typeFor = (it) => typeOf(studioDept(store.get().office, it.deptId), it.kind);
  const infoOf = (it) => statusInfo(typeFor(it), it.status);
  const canOperate = (it) => ctx.can('operate', { floorId: it.floorId, deptId: it.deptId });
  const isAdmin = () => store.get().me?.role === 'admin';
  const isProblem = (it) => infoOf(it).error || it.status === 'review';

  // --- small parts ------------------------------------------------------------------------------
  function chip(it) {
    const info = infoOf(it);
    return h('span', { class: classNames(['st-status', `st-tone-${info.tone}`]) }, h('span', { class: 'st-status-dot', 'aria-hidden': 'true' }), info.label);
  }

  function ref(it) {
    const label = refLabel(it.ref);
    if (!label) return null;
    return h('button', {
      type: 'button', class: 'st-ref st-ref-copy', title: t('studio.ref.copy'), 'aria-label': t('studio.ref.copyLabel', { ref: label }),
      onClick: () => copyText(ctx, label),
    }, label);
  }

  /** The four tools of an element: description, history, « Regénérer » (operate), full sheet. */
  function tools(it, { regen = true, vertical = false } = {}) {
    return h('div', { class: classNames(['sp-tools', { 'is-vertical': vertical }]) },
      iconButton('paragraph', t('studio.pub.details'), { class: 'sp-tool', size: 15, onClick: () => openDetails(it) }),
      iconButton('clock', t('studio.pub.history'), { class: 'sp-tool', size: 15, onClick: () => openHistory(it) }),
      regen && canOperate(it) ? iconButton('refresh', t('studio.regen'), { class: 'sp-tool sp-regen', size: 15, onClick: () => regenerate(it) }) : null,
      it.id === dossier?.pub?.id ? null
        : h('a', { class: 'sp-tool sp-open icon-btn', href: subHash(it.id), title: t('studio.pub.open'), 'aria-label': t('studio.pub.openLabel', { title: it.title }) }, icon('arrowRight', { size: 15 })));
  }

  /** The hash of an element shown inside this ad's page (« ← Pub 9 »). */
  function subHash(id) {
    return routeHash({ name: 'studioItem', id: dossier?.pub?.id || route.id, tab: 'v', sub: id });
  }

  /** A small « ×3 » badge on a medium with several versions: opens them all. */
  function versionsBadge(it, files) {
    if (!files || files.length < 2) return null;
    // An element of the ad: its page lists every version (and those still in their tasks).
    const onClick = it.id === dossier?.pub?.id ? () => openVersions(it, files) : () => ctx.navigate(subHash(it.id));
    return h('button', { type: 'button', class: 'sp-badge', title: t('studio.pub.versions', { n: files.length }), onClick },
      icon('layers', { size: 12 }), String(files.length));
  }

  /** « Gardée avec réserve »: kept by the control of production after its last attempt was refused. */
  function reserveBadge(it) {
    if (!it?.tags?.includes('reserve')) return null;
    return h('span', { class: 'sp-reserve', title: t('studio.pub.reserveHint') }, icon('alert', { size: 12 }), t('studio.pub.reserve'));
  }

  /** The name of an agent for a message (« 5.SCÉNARISTE »), else its id. */
  function agentName(id) {
    return id ? agentById(id)?.name || id : '';
  }

  /**
   * The video frame of a scene without any sequence: left out by the agent who writes them (and
   * who), failed, forced (being written) or not written yet (its status).
   */
  function gapFrame(scene, gap) {
    if (!gap || gap.state === 'pending') {
      return h('div', { class: 'sp-missing' }, icon('film', { size: 18 }), h('p', { class: 'sp-missing-title' }, t('studio.pub.noSequences')), scene ? chip(scene) : null);
    }
    if (gap.state === 'forced') {
      return h('div', { class: 'sp-missing is-forced' }, icon('bolt', { size: 18 }), h('p', { class: 'sp-missing-title' }, t('studio.pub.gap.forced')));
    }
    const who = gap.state === 'skipped' ? agentName(gap.agentId) : '';
    return h('div', { class: 'sp-missing is-problem' }, icon('alert', { size: 18 }),
      h('p', { class: 'sp-missing-title' }, t(gap.state === 'error' ? 'studio.pub.gap.error' : 'studio.pub.gap.skipped')),
      who ? h('p', { class: 'sp-missing-by' }, t('studio.pub.gap.by', { agent: who })) : null);
  }

  /** « Forcer la création » of a scene left without sequence (operate on the scene and the ad), else null. */
  function forceButton(scene, { variant = 'secondary' } = {}) {
    if (!scene || !dossier?.pub || !canOperate(scene) || !canOperate(dossier.pub)) return null;
    return button(t('studio.force.action'), { variant, size: 'sm', icon: 'bolt', class: 'sp-force', title: t('studio.force.tip'), onClick: () => forceScene(scene) });
  }

  /** An image that opens in large in a dialog (no other page). */
  function zoomable(url, alt, cls = 'sp-img') {
    return h('button', { type: 'button', class: classNames(['sp-zoom', cls]), 'aria-label': t('studio.pub.zoom', { name: alt }), onClick: () => openImage(url, alt) },
      h('img', { src: url, alt, loading: 'lazy', decoding: 'async' }));
  }

  function video(file, poster = null) {
    return h('video', { class: 'sp-video', src: file.url, controls: true, preload: poster ? 'none' : 'metadata', playsinline: true, poster: poster || null });
  }

  /** The frame of a missing image or video: its status and, for a problem, why. */
  function missing(it, what) {
    if (!it) return h('div', { class: 'sp-missing' }, icon('film', { size: 18 }), h('p', { class: 'sp-missing-title' }, t('studio.pub.noSequences')));
    const info = infoOf(it);
    return h('div', { class: classNames(['sp-missing', { 'is-problem': isProblem(it) }]) },
      icon(info.error ? 'alert' : what === 'image' ? 'eye' : 'film', { size: 18 }),
      h('p', { class: 'sp-missing-title' }, info.error ? t('studio.pub.missing.error') : t(`studio.pub.missing.${what}`)),
      chip(it));
  }

  /**
   * A section folded by default: its head (title, count, a short summary when closed, a chevron)
   * opens it; the body is built only when open. The choice is remembered — unless the section
   * says itself whether it is open (`isOpen`, `onToggle`: the brief's).
   */
  function fold(key, title, { count = null, summary = null, body, id = null, isOpen: forced = null, onToggle = null }) {
    const isOpen = forced ?? open.has(key);
    const head = h('button', { type: 'button', class: 'sp-fold-head', 'aria-expanded': String(isOpen), onClick: () => {
      if (onToggle) onToggle(!isOpen);
      else if (open.has(key)) open.delete(key);
      else open.add(key);
      if (!onToggle) writeOpen(open);
      render();
    } },
    h('span', { class: 'st-section-title' }, title, count != null ? h('span', { class: 'st-section-count' }, String(count)) : null),
    !isOpen && summary ? h('span', { class: 'sp-fold-summary' }, summary) : h('span', { class: 'sp-fold-summary' }),
    h('span', { class: 'sp-fold-icon', 'aria-hidden': 'true' }, icon(isOpen ? 'minus' : 'plus', { size: 16 })));
    return h('section', { class: classNames(['sp-section', 'sp-fold', `sp-fold-${key}`, { 'is-open': isOpen }]), id }, head, isOpen ? body() : null);
  }

  // --- dialogs --------------------------------------------------------------------------------
  function openImage(url, alt) {
    ctx.overlay.openModal({ title: alt, size: 'lg', className: 'sp-lightbox', content: h('div', { class: 'sp-lightbox-body' }, h('img', { src: url, alt })) });
  }

  function fieldRows(it) {
    return describeFields(it, typeFor(it)).map((f) => h('div', { class: 'st-field' },
      h('dt', { class: 'st-field-label mono-label' }, f.label),
      h('dd', { class: 'st-field-value' }, f.url
        ? h('a', { href: f.url, target: '_blank', rel: 'noopener noreferrer', class: 'st-link' }, f.text)
        : h('p', { class: f.long ? 'st-longtext' : f.json ? 'st-json' : 'st-value' }, f.text))));
  }

  function openDetails(it) {
    const rows = fieldRows(it);
    const reason = isProblem(it) ? waitingReason(it) : '';
    const head = h('div', { class: 'sp-dialog-head' }, chip(it), ref(it), h('span', { class: 'st-meta mono-label', title: formatDateTime(it.updatedAt) }, t('studio.updatedAgo', { when: relativeTime(it.updatedAt) })));
    ctx.overlay.openModal({
      title: it.title, size: 'lg',
      content: h('div', { class: 'sp-dialog' }, head,
        reason ? h('p', { class: 'sp-card-reason' }, reason) : null,
        rows.length ? h('dl', { class: 'st-fields' }, ...rows) : h('p', { class: 'st-muted' }, t('studio.noFields'))),
    });
  }

  function openHistory(it) {
    ctx.overlay.openModal({
      title: t('studio.pub.historyOf', { title: it.title }), size: 'md',
      content: it.history?.length ? historyList(it, typeFor(it), agentById, { dated: true }) : h('p', { class: 'st-muted' }, t('studio.pub.noHistory')),
    });
  }

  function openVersions(it, files) {
    const list = [...files].reverse().map((f, i) => h('figure', { class: 'sp-version' },
      f.media === 'video' ? video(f) : zoomable(f.url, f.name),
      h('figcaption', { class: 'sp-version-cap' },
        h('span', { class: 'mono-label' }, i === 0 ? t('studio.pub.latest') : t('studio.pub.older', { n: files.length - i })),
        h('span', { class: 'sp-version-name', title: f.name }, f.name),
        f.addedAt ? h('time', { class: 'mono-label', title: formatDateTime(f.addedAt) }, relativeTime(f.addedAt)) : null)));
    ctx.overlay.openModal({ title: t('studio.pub.versionsOf', { title: it.title }), size: 'lg', content: h('div', { class: 'sp-versions-grid' }, ...list) });
  }

  async function regenerate(it) {
    const type = typeFor(it);
    const dept = deptById(it.deptId);
    const agent = agentById(it.agentId || type?.agentId) || agentById(dept?.leadAgentId);
    const reason = await ctx.overlay.prompt({
      title: t('studio.regen.title', { title: it.title }),
      label: t('studio.regen.label'),
      hint: agent ? t('studio.regen.hint', { agent: agent.name }) : t('studio.regen.hintLead'),
      multiline: true, maxLength: 2000, confirmLabel: t('studio.regen.confirm'),
    });
    if (!reason || destroyed) return;
    try {
      const out = await ctx.api.post(`/api/studio/items/${enc(it.id)}/regenerate`, { reason });
      ctx.toast.success(t('studio.regen.done'), { action: { label: t('studio.regen.open'), onClick: () => ctx.navigate(`#/task/${enc(out.taskId)}`) } });
      load();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  /**
   * « Forcer la création »: the agent who writes the sequences writes this scene's this time
   * (rewording what the brand cannot claim rather than leaving it out); the ad is remade with it
   * once its video is validated.
   */
  async function forceScene(scene) {
    const gap = sceneGap({ scene, sequences: [] }, infoOf(scene));
    const who = agentName(gap?.agentId);
    const note = await ctx.overlay.prompt({
      title: t('studio.force.title', { title: scene.title }),
      label: t('studio.force.label'),
      hint: who ? t('studio.force.hint', { agent: who }) : t('studio.force.hintLead'),
      multiline: true, required: false, maxLength: 2000, confirmLabel: t('studio.force.confirm'),
    });
    if (note === null || destroyed) return;
    try {
      const out = await ctx.api.post(`/api/studio/items/${enc(scene.id)}/force`, { pubId: dossier?.pub?.id, note: note.trim() || undefined });
      ctx.toast.success(t('studio.force.done'), { action: { label: t('studio.regen.open'), onClick: () => ctx.navigate(`#/task/${enc(out.taskId)}`) } });
      load();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  // --- blocks ---------------------------------------------------------------------------------
  function crumbs(pub) {
    const dept = deptById(pub.deptId);
    const sep = () => h('span', { class: 'st-crumb-sep', 'aria-hidden': 'true' }, icon('chevronRight', { size: 13 }));
    return h('nav', { class: 'st-crumbs', 'aria-label': t('studio.crumbsLabel') },
      h('a', { class: 'st-crumb st-crumb-root', href: studioHash(floors(), pub.floorId, null) }, icon('film', { size: 14 }), t('studio.title')),
      dept ? sep() : null,
      dept ? h('a', { class: 'st-crumb', href: studioHash(floors(), pub.floorId, dept.id) }, dept.name) : null,
      sep(),
      h('span', { class: 'st-crumb is-current', 'aria-current': 'page' }, pub.title));
  }

  function header(pub) {
    const type = typeFor(pub);
    const info = infoOf(pub);
    let status = chip(pub);
    if (canOperate(pub) && type) {
      const sel = select(type.statuses.map((s) => ({ value: s.key, label: s.label })), { class: classNames(['st-status-select', `st-tone-${info.tone}`]), value: pub.status, 'aria-label': t('studio.status') });
      sel.addEventListener('change', () => changeStatus(pub, sel));
      status = h('label', { class: classNames(['st-status-edit', `st-tone-${info.tone}`]) }, h('span', { class: 'st-status-dot', 'aria-hidden': 'true' }), sel);
    }
    const dept = deptById(pub.deptId);
    return h('header', { class: 'sp-head' },
      h('div', { class: 'sp-head-titles' },
        h('p', { class: 'st-item-kind mono-label' }, type?.label || pub.kind),
        h('div', { class: 'st-item-titleline' }, h('h3', { class: 'st-item-title' }, pub.title), ref(pub)),
        h('div', { class: 'st-item-meta' },
          status,
          h('span', { class: 'st-dept', style: { '--dept': deptColor(dept?.color, pub.deptId) } }, h('span', { class: 'st-dept-dot', 'aria-hidden': 'true' }), dept?.name || pub.deptName || ''),
          h('span', { class: 'st-meta mono-label', title: formatDateTime(pub.updatedAt) }, t('studio.updatedAgo', { when: relativeTime(pub.updatedAt) })))),
      h('div', { class: 'st-actions' },
        canOperate(pub) ? button(t('studio.pub.remake'), { variant: 'primary', size: 'sm', icon: 'refresh', onClick: () => regenerate(pub) }) : null,
        isAdmin() ? button(t('studio.delete'), { variant: 'ghost', size: 'sm', onClick: () => remove(pub) }) : null));
  }

  /** « Notre pub »: the video on the left (or an empty frame), its state on the right. */
  function resultBlock(d) {
    const pub = d.pub;
    const videos = pubVideos(pub);
    const stage = h('div', { class: 'sp-result-media' });
    let switcher = null;
    if (videos.length) {
      const show = (v) => {
        stage.replaceChildren(video(v.file), versionsBadge(pub, mediaFiles(pub, 'video')) || '');
        for (const b of switcher?.querySelectorAll('.segmented-opt') || []) b.setAttribute('aria-pressed', String(b.dataset.key === v.key));
      };
      if (videos.length > 1) {
        switcher = h('div', { class: 'segmented segmented-sm sp-switch', role: 'group', 'aria-label': t('studio.pub.versionLabel') },
          ...videos.map((v) => h('button', { type: 'button', class: 'segmented-opt', dataset: { key: v.key }, 'aria-pressed': 'false', onClick: () => show(v) }, t(`studio.pub.video.${v.key}`))));
      }
      show(videos[0]);
    } else {
      stage.append(h('div', { class: 'sp-empty-frame' }, icon('film', { size: 26 }), h('p', { class: 'sp-empty-title' }, t('studio.pub.noVideo'))));
    }
    const side = h('div', { class: 'sp-result-side' },
      switcher,
      decisionBlock(pub, videos.length > 0),
      videos.length ? null : h('p', { class: 'sp-empty-why' }, whyOf(d)),
      progressBlock(d),
      stopsBlock(d),
      gapsBlock(d),
      watchBlock(d),
      videos.length ? h('div', { class: 'sp-downloads' }, ...videos.map((v) => h('a', { class: 'st-file-dl', href: v.file.url, download: v.file.name }, `${t('studio.download')} · ${t(`studio.pub.video.${v.key}`)}`))) : null,
      tools(pub, { regen: false }));
    return h('section', { class: 'sp-section sp-result' },
      h('h4', { class: 'st-section-title' }, t('studio.pub.result')),
      h('div', { class: 'sp-result-grid' }, stage, side));
  }

  /**
   * The owner's decision on a finished ad (status « À valider »): « Valider la pub » (Prête),
   * « Demander des changements » (its montage agent remakes it with the owner's words) and, when
   * the studio has that status, « Refuser la pub ».
   */
  function decisionBlock(pub, hasVideo) {
    const type = typeFor(pub);
    if (!type || pub.status !== 'review' || !canOperate(pub)) return null;
    const keys = new Set(type.statuses.map((x) => x.key));
    const ready = keys.has('ready') ? 'ready' : keys.has('published') ? 'published' : null;
    const approve = ready ? button(t('studio.pub.approve'), { variant: 'primary', size: 'sm', icon: 'check', class: 'sp-approve' }) : null;
    approve?.addEventListener('click', () => setPubStatus(pub, ready, approve, t('studio.pub.approved')));
    const refuse = keys.has('rejected') ? button(t('studio.pub.refuse'), { variant: 'ghost', size: 'sm', class: 'sp-refuse-pub' }) : null;
    refuse?.addEventListener('click', async () => {
      const ok = await ctx.overlay.confirm({ title: t('studio.pub.refuseTitle', { title: pub.title }), message: t('studio.pub.refuseMessage'), confirmLabel: t('studio.pub.refuse'), danger: true });
      if (ok && !destroyed) setPubStatus(pub, 'rejected', refuse, t('studio.pub.refused'));
    });
    return h('div', { class: 'sp-decision' },
      h('p', { class: 'sp-decision-title' }, icon('check', { size: 15 }), hasVideo ? t('studio.pub.decide') : t('studio.pub.decideNoVideo')),
      h('div', { class: 'sp-decision-actions' },
        approve,
        button(t('studio.pub.changes'), { variant: 'secondary', size: 'sm', icon: 'pencil', onClick: () => regenerate(pub) }),
        refuse));
  }

  async function setPubStatus(pub, status, btn, done) {
    if (btn) btn.disabled = true;
    try {
      await ctx.api.patch(`/api/studio/items/${enc(pub.id)}`, { status });
      ctx.toast.success(done, { timeout: 3000 });
      load();
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  /**
   * « Contrôle de production »: what the office's own control found (why the ad waits, who it
   * asked and when, the quality check of the finished ad) and « Relancer maintenant ».
   */
  function watchBlock(d) {
    const w = d.watch;
    if (!w || (!w.alert && !w.followUps?.length && !w.qa)) return null;
    const rows = [];
    if (w.alert?.problem) rows.push(h('ul', { class: 'sp-watch-lines' }, ...w.alert.lines.slice(0, 5).map((l) => h('li', {}, l))));
    for (const f of (w.followUps || []).slice(-3).reverse()) {
      rows.push(h('p', { class: 'sp-watch-row' },
        icon('refresh', { size: 13 }),
        h('span', {}, t(f.level === 'director' ? 'studio.watch.director' : 'studio.watch.followUp', { agent: f.agentName || f.agentId, when: relativeTime(f.at) })),
        f.taskId ? h('a', { class: 'st-link', href: `#/task/${enc(f.taskId)}` }, t('studio.watch.task')) : null));
    }
    if (w.qa) {
      rows.push(h('p', { class: 'sp-watch-row' }, icon('eye', { size: 13 }),
        h('span', {}, t('studio.watch.qa', { agent: w.qa.agentName || '', when: relativeTime(w.qa.at) })),
        w.qa.taskId ? h('a', { class: 'st-link', href: `#/task/${enc(w.qa.taskId)}` }, t('studio.watch.task')) : null));
    }
    if (w.alert?.exhausted) rows.push(h('p', { class: 'sp-watch-row is-warn' }, icon('alert', { size: 13 }), t('studio.watch.exhausted')));
    let kick = null;
    if (w.alert?.problem && canOperate(d.pub)) {
      kick = button(t('studio.watch.kick'), { variant: 'secondary', size: 'sm', icon: 'refresh' });
      kick.addEventListener('click', async () => {
        kick.disabled = true;
        try {
          const out = await ctx.api.post(`/api/studio/watch/${enc(d.pub.id)}/kick`, {});
          ctx.toast.success(out?.followUp ? t('studio.watch.kicked') : (out?.message || t('studio.watch.nothing')), { timeout: 3500 });
          load();
        } catch (err) {
          ctx.toast.error(err);
        } finally {
          kick.disabled = false;
        }
      });
    }
    return h('div', { class: classNames(['sp-watch', { 'is-problem': Boolean(w.alert?.problem) }]) },
      h('p', { class: 'sp-watch-title mono-label' }, t('studio.watch.title')), ...rows, kick);
  }

  /**
   * Why our ad has no video yet: while the script of its brief is not approved, where the script
   * stands (« Le script attend ta validation… »), else what its own status means.
   */
  function whyOf(d) {
    const phase = d.origin ? briefPhase(d.origin, typeFor(d.origin)) : null;
    if (phase) {
      const why = t(`studio.pub.why.brief.${phase}`);
      return why === `studio.pub.why.brief.${phase}` ? t('studio.pub.why.brief.other', { status: infoOf(d.origin).label }) : why;
    }
    const why = t(`studio.pub.why.${d.pub.status}`);
    return why === `studio.pub.why.${d.pub.status}` ? infoOf(d.pub).label : why;
  }

  function progressBlock(d) {
    const p = pubProgress(d);
    if (!p.scenes.total && !p.sequences.total) return null;
    const line = (label, a, b) => h('li', { class: 'sp-progress-row' },
      h('span', { class: 'sp-progress-label' }, label),
      h('span', { class: 'sp-progress-bar', 'aria-hidden': 'true' }, h('span', { style: { width: `${b ? Math.round((100 * a) / b) : 0}%` } })),
      h('span', { class: 'sp-progress-n mono-label' }, `${a}/${b}`));
    return h('ul', { class: 'sp-progress' },
      line(t('studio.pub.p.scenes'), p.scenes.done, p.scenes.total),
      line(t('studio.pub.p.images'), p.images.done, p.images.total),
      line(t('studio.pub.p.sequences'), p.sequences.done, p.sequences.total));
  }

  function stopsBlock(d) {
    const stop = blockers(d);
    if (!stop.length) return null;
    return h('div', { class: 'sp-stops' },
      h('p', { class: 'sp-stops-title' }, icon('alert', { size: 15 }), t('studio.pub.stops', { n: stop.length })),
      h('ul', {}, ...stop.map((s) => h('li', {},
        h('button', { type: 'button', class: 'sp-stop', onClick: () => focusCard(s.item.id) }, chip(s.item), h('span', { class: 'sp-stop-title' }, s.item.title)),
        s.reason ? h('p', { class: 'sp-stop-reason' }, s.reason) : null))));
  }

  /**
   * The scenes the ad goes without — left out by the agent who writes the sequences (with why and
   * who) or failed — each with « Forcer la création »; and the ones being made after a force.
   */
  function gapsBlock(d) {
    const { missing: gaps, forced } = sceneGaps(d, infoOf);
    if (!gaps.length && !forced.length) return null;
    const made = Boolean(pubSequenceIds(d.pub));
    const rows = gaps.map((g) => h('li', {},
      h('button', { type: 'button', class: 'sp-stop', onClick: () => focusCard(g.scene.id) }, chip(g.scene), h('span', { class: 'sp-stop-title' }, g.scene.title)),
      h('p', { class: 'sp-stop-reason' },
        g.state === 'skipped' && g.agentId ? h('strong', {}, `${t('studio.pub.gap.by', { agent: agentName(g.agentId) })} · `) : null,
        g.reason || t('studio.pub.gap.noReason')),
      forceButton(g.scene)));
    return h('div', { class: 'sp-gaps' },
      gaps.length ? h('p', { class: 'sp-gaps-title' }, icon('alert', { size: 15 }), t(made ? 'studio.pub.gaps.made' : 'studio.pub.gaps.title', { n: gaps.length })) : null,
      gaps.length ? h('ul', {}, ...rows) : null,
      forced.length ? h('p', { class: 'sp-gaps-forced' }, icon('bolt', { size: 13 }), t('studio.pub.gaps.forced', { titles: forced.map((x) => x.title).join(', ') })) : null);
  }

  /** « Vient de »: a small thumbnail of the competitor's ad (or the brief) and, beside it, its title and summary. */
  function originBlock(d) {
    const o = d.origin;
    if (!o) return h('section', { class: 'sp-section sp-origin' }, h('h4', { class: 'st-section-title' }, t('studio.pub.origin')), h('p', { class: 'st-muted' }, t('studio.pub.noOrigin')));
    const vid = mediaFiles(o, 'video').find((f) => f.role === 'source') || latestFinal(o, 'video') || mediaFiles(o, 'video')[0];
    const img = mediaFiles(o, 'image')[0];
    const competitor = o.kind === 'source';
    const thumb = vid ? h('div', { class: 'sp-origin-thumb' }, video(vid)) : img ? zoomable(img.url, o.title, 'sp-origin-thumb') : null;
    const summary = String(o.data?.summary || o.data?.hook || o.data?.brief || o.data?.script || o.data?.message || '');
    return h('section', { class: 'sp-section sp-origin' },
      h('h4', { class: 'st-section-title' }, competitor ? t('studio.pub.originAd') : t('studio.pub.originBrief')),
      h('div', { class: classNames(['sp-origin-grid', { 'no-thumb': !thumb }]) },
        thumb,
        h('div', { class: 'sp-origin-text' },
          h('button', { type: 'button', class: 'sp-origin-title', onClick: () => openDetails(o) }, h('span', {}, o.title)),
          h('div', { class: 'sp-origin-meta' }, ref(o), chip(o), o.data?.brand ? h('span', { class: 'st-meta' }, o.data.brand) : null),
          o.data?.adUrl ? h('a', { class: 'st-meta st-ext', href: o.data.adUrl, target: '_blank', rel: 'noopener noreferrer' }, icon('arrowRight', { size: 14 }), t('studio.pub.adLink')) : null,
          summary ? h('p', { class: 'sp-origin-summary' }, summary.length > 420 ? `${summary.slice(0, 420).trimEnd()}…` : summary) : null,
          tools(o))));
  }

  // --- the brief of an ad on brief ----------------------------------------------------------------

  /** « Valider le script » / « Demander des retouches » (operate, brief in validation), else nothing. */
  function briefButtons(o) {
    if (!canOperate(o)) return null;
    const actions = briefActions(typeFor(o), o.status);
    if (!actions.length) return null;
    const approve = actions.includes('approved') ? button(t('studio.pub.brief.approve'), { variant: 'primary', size: 'sm', icon: 'check', class: 'sp-brief-approve' }) : null;
    approve?.addEventListener('click', () => approveBrief(o, approve));
    return h('div', { class: 'sp-brief-actions' },
      approve,
      actions.includes('rework') ? button(t('studio.pub.brief.rework'), { variant: 'secondary', size: 'sm', icon: 'pencil', class: 'sp-brief-rework', onClick: () => askRework(o) }) : null);
  }

  /** The owner's rework of a brief sent back to the concepteur (shown while it is in rework). */
  function feedbackBox(o) {
    const text = o.status === 'rework' && typeof o.data?.feedback === 'string' ? o.data.feedback.trim() : '';
    if (!text) return null;
    return h('div', { class: 'sp-brief-feedback' }, h('p', { class: 'sp-brief-feedback-title mono-label' }, t('studio.pub.brief.feedback')), h('p', { class: 'sp-brief-feedback-text' }, text));
  }

  /** Beside our ad, for an ad on brief: the brief's status, its ref, where the script stands and what to decide. */
  function briefCard(o) {
    const next = t(`studio.pub.brief.next.${o.status}`);
    return h('section', { class: 'sp-section sp-brief-card' },
      h('h4', { class: 'st-section-title' }, t('studio.pub.brief')),
      h('button', { type: 'button', class: 'sp-origin-title', title: t('studio.pub.brief.open'), onClick: () => focusCard(o.id) }, h('span', {}, o.title)),
      h('div', { class: 'sp-origin-meta' }, ref(o), chip(o)),
      next === `studio.pub.brief.next.${o.status}` ? null : h('p', { class: 'sp-brief-next' }, next),
      feedbackBox(o),
      briefButtons(o));
  }

  /**
   * « Brief », full width under the top: every field of its type in order (long texts folded,
   * « Modifier » while the concepteur is not on it), its files (references added or removed), its
   * history; open while the script is not approved, folded once it is (a click overrides it).
   */
  function briefSection(o) {
    const type = typeFor(o);
    const phase = briefPhase(o, type);
    const isOpen = briefFold && briefFold.status === o.status ? briefFold.open : Boolean(phase) || editing;
    const idea = String(o.data?.brief || o.data?.message || '').replace(/\s+/g, ' ').trim();
    const summary = h('span', { class: 'sp-fold-line' }, chip(o), idea ? h('span', { class: 'sp-fold-text' }, idea) : null);
    return fold('brief', t('studio.pub.brief'), {
      id: `sp-${o.id}`,
      summary,
      isOpen,
      onToggle: (next) => { briefFold = { status: o.status, open: next }; },
      body: () => h('div', { class: 'sp-fold-body sp-brief' },
        feedbackBox(o),
        h('div', { class: 'sp-brief-grid' },
          h('div', { class: 'sp-brief-main' }, briefFields(o, type), briefButtons(o)),
          h('div', { class: 'sp-brief-side' },
            h('h5', { class: 'sp-brief-sub mono-label' }, t('studio.files'), o.files.length ? h('span', { class: 'st-section-count' }, String(o.files.length)) : null),
            filesPanel(ctx, o, { operate: canOperate(o), roles: fileRoles('brief'), onChanged: () => load(), isDestroyed: () => destroyed }),
            h('h5', { class: 'sp-brief-sub mono-label' }, t('studio.history')),
            o.history?.length ? historyList(o, type, agentById, { dated: true }) : h('p', { class: 'st-muted' }, t('studio.pub.noHistory'))))),
    });
  }

  /** The brief's fields, or its form while « Modifier » is open (kept across renders). */
  function briefFields(o, type) {
    if (editing && type) {
      editNode ||= editForm(ctx, o, type, {
        onSaved: () => stopEditing(true),
        onCancel: () => stopEditing(false),
      });
      return editNode;
    }
    const rows = itemFieldRows(o, type, { empty: true });
    let edit = null;
    if (canOperate(o) && type) {
      edit = briefEditable(o)
        ? button(t('studio.edit'), { variant: 'ghost', size: 'sm', icon: 'pencil', class: 'st-edit', onClick: () => { editing = true; render(); } })
        : h('p', { class: 'st-muted sp-brief-locked' }, t('studio.pub.brief.locked'));
    }
    return h('div', { class: 'sp-brief-fields' },
      h('div', { class: 'st-section-head' }, h('h5', { class: 'sp-brief-sub mono-label' }, t('studio.fields')), edit),
      rows.length ? h('dl', { class: 'st-fields' }, ...rows) : h('p', { class: 'st-muted' }, t('studio.noFields')));
  }

  /** Closes « Modifier »: reload after a save, or when live changes waited meanwhile. */
  function stopEditing(saved) {
    editing = false;
    editNode = null;
    if (saved || missed) {
      missed = false;
      load();
    } else {
      render();
    }
  }

  async function approveBrief(o, btn) {
    if (btn) btn.disabled = true;
    try {
      await ctx.api.patch(`/api/studio/items/${enc(o.id)}`, { status: 'approved' });
      ctx.toast.success(t('studio.pub.brief.approved'), { timeout: 2500 });
      load();
    } catch (err) {
      ctx.toast.error(err);
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  /** « Demander des retouches »: the owner's feedback, then one PATCH (rework + feedback + note). */
  async function askRework(o) {
    const text = await ctx.overlay.prompt({
      title: t('studio.pub.brief.reworkTitle'),
      label: t('studio.pub.brief.reworkLabel'),
      hint: t('studio.pub.brief.reworkHint'),
      multiline: true, maxLength: 2000, confirmLabel: t('studio.pub.brief.reworkConfirm'),
    });
    const feedback = typeof text === 'string' ? text.trim() : '';
    if (!feedback || destroyed) return;
    const patch = { status: 'rework', note: feedback };
    if ((typeFor(o)?.fields || []).some((f) => f.key === 'feedback')) patch.data = { feedback };
    try {
      await ctx.api.patch(`/api/studio/items/${enc(o.id)}`, patch);
      ctx.toast.success(t('studio.pub.brief.reworkDone'), { timeout: 3000 });
      load();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  /** « Découpe du concurrent »: folded to a strip of small captures; open, one tile per scene. */
  function scenesBlock(d) {
    const caps = d.scenes.map(({ scene }) => ({ scene, cap: captureOf(scene) }));
    const strip = h('span', { class: 'sp-strip' }, ...caps.slice(0, 16).map(({ scene, cap }) => (cap
      ? h('img', { class: 'sp-strip-img', src: cap.url, alt: '', loading: 'lazy', title: scene.title })
      : h('span', { class: 'sp-strip-img sp-noimg' }))));
    return fold('cut', t('studio.pub.cut'), {
      count: caps.length,
      summary: strip,
      body: () => h('div', { class: 'sp-fold-body' },
        h('p', { class: 'sp-hint' }, t('studio.pub.cutHint')),
        h('div', { class: 'sp-scenes' }, ...caps.map(({ scene, cap }) => h('div', { class: 'sp-scene', id: `sp-${scene.id}` },
          cap ? zoomable(cap.url, scene.title, 'sp-scene-img') : h('div', { class: 'sp-scene-img sp-noimg' }, icon('eye', { size: 18 })),
          h('div', { class: 'sp-scene-body' },
            h('p', { class: 'sp-card-title', title: scene.title }, scene.title),
            h('div', { class: 'sp-card-meta' }, scene.data?.timecode ? h('span', { class: 'mono-label' }, scene.data.timecode) : null),
            h('div', { class: 'sp-card-meta' }, chip(scene), gapBadge(scene)),
            tools(scene)))))),
    });
  }

  /** « Écartée » / « Création forcée » on a scene of the competitor's cut, else null. */
  function gapBadge(scene) {
    const entry = dossier?.scenes.find((s) => s.scene.id === scene.id);
    const gap = entry ? sceneGap(entry, infoOf(scene)) : null;
    if (gap?.state === 'skipped' || gap?.state === 'error') return h('span', { class: 'sp-reserve sp-gap-badge', title: gap.reason || '' }, icon('alert', { size: 12 }), t('studio.pub.gap.badge'));
    if (isForced(scene)) return h('span', { class: 'sp-reserve sp-forced-badge' }, icon('bolt', { size: 12 }), t('studio.pub.gap.forcedBadge'));
    return null;
  }

  /** The status of an element as a small menu (operate) — « Refusée » keeps it out of every montage. */
  function statusMenu(it) {
    const type = typeFor(it);
    if (!type || !canOperate(it)) return chip(it);
    const info = infoOf(it);
    const sel = select(type.statuses.map((x) => ({ value: x.key, label: x.label })), { class: classNames(['st-status-select', `st-tone-${info.tone}`]), value: it.status, 'aria-label': t('studio.status') });
    sel.addEventListener('change', async () => {
      sel.disabled = true;
      try {
        await ctx.api.patch(`/api/studio/items/${enc(it.id)}`, { status: sel.value });
        ctx.toast.success(t('studio.statusChanged', { label: statusInfo(type, sel.value).label }), { timeout: 2500 });
        load();
      } catch (err) {
        ctx.toast.error(err);
        sel.value = it.status;
        sel.disabled = false;
      }
    });
    return h('label', { class: classNames(['st-status-edit', 'sp-status-menu', `st-tone-${info.tone}`]) }, h('span', { class: 'st-status-dot', 'aria-hidden': 'true' }), sel);
  }

  /** Put a sequence back into the ad's montage, or take it out (the sequence itself stays). */
  async function setInPub(sq, inside) {
    const pub = dossier?.pub;
    if (!pub) return;
    try {
      await ctx.api.patch(`/api/studio/items/${enc(pub.id)}`, inside
        ? { addParents: [sq.id], note: `Séquence remise dans la pub : ${sq.title}` }
        : { removeParents: [sq.id], note: `Séquence retirée de la pub : ${sq.title}` });
      ctx.toast.success(inside ? t('studio.pub.inDone') : t('studio.pub.outDone'), { timeout: 4000 });
      load();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  /** « Dans la pub » / « Hors pub » on a sequence of an assembled ad (« Ajoutée une fois validée » after a force). */
  function inPubToggle(sq, scene = null) {
    const pub = dossier?.pub;
    if (!sq || !pub || !pubSequenceIds(pub) || sq.status === 'rejected') return null;
    const inside = usedInPub(pub, sq);
    if (!inside && goesInPub(pub, scene, sq)) return h('span', { class: 'sp-inpub is-coming', title: t('studio.pub.comingHint') }, icon('bolt', { size: 13 }), t('studio.pub.coming'));
    const label = inside ? t('studio.pub.inPub') : t('studio.pub.outOfPub');
    if (!canOperate(pub)) return h('span', { class: classNames(['sp-inpub', { 'is-out': !inside }]) }, label);
    return h('button', {
      type: 'button', class: classNames(['sp-inpub', { 'is-out': !inside }]), 'aria-pressed': String(inside),
      title: inside ? t('studio.pub.takeOut') : t('studio.pub.putBack'),
      onClick: () => setInPub(sq, !inside),
    }, icon(inside ? 'check' : 'minus', { size: 13 }), label);
  }

  /** One sequence: its start image and, beside it, its video, each with its tools below; its line and why it waits under both. */
  function seqBox(box) {
    const { scene, image, sequence: sq } = box;
    const imgFiles = image ? mediaFiles(image, 'image') : [];
    const imgLast = image ? latestFinal(image, 'image') || imgFiles[imgFiles.length - 1] : null;
    const vidFiles = sq ? mediaFiles(sq, 'video') : [];
    const vidLast = sq ? latestFinal(sq, 'video') || vidFiles[vidFiles.length - 1] : null;
    const line = sq?.data?.line ? String(sq.data.line).replace(/^VOICE-OVER:\s*/i, '') : '';
    const out = Boolean(sq) && !goesInPub(dossier?.pub, scene, sq);
    const problem = out ? null : [image, sq].filter(Boolean).find(isProblem);
    // A scene without any sequence: why (left out, failed, forced) and « Forcer la création ».
    const gap = !sq && scene ? sceneGap({ scene, sequences: [] }, infoOf(scene)) : null;
    const gapProblem = gap?.state === 'skipped' || gap?.state === 'error';
    const reason = problem ? waitingReason(problem) : gap && gap.state !== 'pending' ? gap.reason : '';
    const title = sq ? sq.title : scene.title;
    // A remix's sequence (no scene of this ad): its video alone, no empty image frame.
    const alone = !scene && !image;
    const target = sq || image || scene;
    return h('div', { class: classNames(['sp-seq', { 'is-problem': Boolean(problem) || gapProblem, 'is-out': out, 'is-alone': alone }]), id: sq ? `sp-${sq.id}` : null },
      h('div', { class: 'sp-seq-head' },
        target ? h('a', { class: 'sp-seq-title', title, href: subHash(target.id) }, title) : h('p', { class: 'sp-seq-title', title }, title),
        sq ? h('div', { class: 'sp-seq-state' }, reserveBadge(sq), statusMenu(sq), inPubToggle(sq, scene)) : null),
      h('div', { class: 'sp-seq-pair' },
      alone ? null : h('div', { class: 'sp-seq-row', id: image ? `sp-${image.id}` : null },
        h('div', { class: 'sp-seq-media' },
          image && imgLast ? zoomable(imgLast.url, image.title, 'sp-seq-fill') : missing(image, 'image'),
          h('span', { class: 'sp-seq-tag mono-label' }, t('studio.pub.ourImage')),
          image ? versionsBadge(image, imgFiles) : null),
        image ? tools(image) : null),
      h('div', { class: 'sp-seq-row' },
        h('div', { class: 'sp-seq-media' },
          sq && vidLast ? video(vidLast, imgLast?.url || null) : sq ? missing(sq, 'video') : gapFrame(scene, gap),
          h('span', { class: 'sp-seq-tag mono-label' }, t('studio.pub.ourSequence')),
          sq ? versionsBadge(sq, vidFiles) : null),
        sq ? tools(sq) : null)),
      line ? h('p', { class: 'sp-card-line', title: line }, `« ${line} »`) : null,
      reason ? h('p', { class: 'sp-card-reason', title: reason }, reason) : null,
      gapProblem ? forceButton(scene) : null);
  }

  /** « Nos séquences »: folded to a count and small images; open, one box per sequence. */
  function oursBlock(d) {
    const boxes = sequenceBoxes(d);
    const p = pubProgress(d);
    const thumbs = boxes.map((b) => b.image && latestFinal(b.image, 'image')).filter(Boolean)
      .filter((f, i, all) => all.findIndex((x) => x.url === f.url) === i).slice(0, 16);
    const summary = h('span', { class: 'sp-fold-line' },
      h('span', { class: 'mono-label' }, t('studio.pub.seqSummary', { done: p.sequences.done, total: p.sequences.total })),
      h('span', { class: 'sp-strip' }, ...thumbs.map((f) => h('img', { class: 'sp-strip-img', src: f.url, alt: '', loading: 'lazy' }))));
    return fold('ours', t('studio.pub.ours'), {
      count: boxes.filter((b) => b.sequence).length,
      summary,
      body: () => (boxes.length
        ? h('div', { class: 'sp-fold-body' }, h('div', { class: 'sp-seqs' }, ...boxes.map(seqBox)))
        : h('div', { class: 'sp-fold-body' }, h('p', { class: 'st-muted' }, t('studio.pub.noScenes')))),
    });
  }

  /** « Informations »: the fields of our ad (script, duration…), folded. */
  function infoBlock(pub) {
    const rows = fieldRows(pub);
    return fold('info', t('studio.fields'), {
      count: rows.length || null,
      body: () => h('div', { class: 'sp-fold-body sp-info' }, rows.length ? h('dl', { class: 'st-fields' }, ...rows) : h('p', { class: 'st-muted' }, t('studio.pub.noInfo'))),
    });
  }

  function focusCard(id) {
    let node = el.querySelector(`#sp-${CSS.escape(id)}`);
    if (id === dossier?.origin?.id && dossier.origin.kind === 'brief' && !node?.classList.contains('is-open')) {
      // The brief's section: opened (by hand) before going to it.
      briefFold = { status: dossier.origin.status, open: true };
      render();
      node = el.querySelector(`#sp-${CSS.escape(id)}`);
    } else if (!node) {
      // The element sits in a folded section: open the one that holds it, then go to it.
      const inCut = dossier?.scenes.some((s) => s.scene.id === id);
      open.add(inCut ? 'cut' : 'ours');
      writeOpen(open);
      render();
      node = el.querySelector(`#sp-${CSS.escape(id)}`);
    }
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    node.classList.remove('is-flash');
    void node.offsetWidth;
    node.classList.add('is-flash');
  }

  // --- one element of the ad (« ← Pub 9 ») ------------------------------------------------------

  /** An element of this ad's dossier (its origin, scenes, images, sequences), or null. */
  function findInDossier(id) {
    const d = dossier;
    if (!d || !id) return null;
    if (d.pub.id === id) return d.pub;
    if (d.origin?.id === id) return d.origin;
    for (const { scene, images, sequences } of d.scenes) {
      if (scene.id === id) return scene;
      const hit = images.find((x) => x.id === id) || sequences.find((x) => x.id === id);
      if (hit) return hit;
    }
    return d.extra.find((x) => x.id === id) || null;
  }

  /** A sequence's start image (the image of this ad it was made from), else null. */
  function startImageOf(it) {
    if (it.kind !== 'sequence') return null;
    const ids = new Set((it.parents || []).map((p) => (typeof p === 'string' ? p : p.id)));
    for (const { images } of dossier?.scenes || []) {
      const im = images.find((x) => ids.has(x.id));
      if (im) return im;
    }
    return null;
  }

  /** Back to the ad's page (the element's entry replaced: « Retour » does not come back here). */
  function backToPub() {
    ctx.navigate(routeHash({ name: 'studioItem', id: dossier?.pub?.id || route.id }), { replace: true });
  }

  async function loadTakes(it) {
    try {
      const out = await ctx.api.get(`/api/studio/items/${enc(it.id)}/takes`);
      if (destroyed) return;
      takesOf.set(it.id, out.takes || []);
    } catch {
      takesOf.set(it.id, []);
    }
    if (sub === it.id) render();
  }

  async function useTake(it, take, btn) {
    btn.disabled = true;
    try {
      const out = await ctx.api.post(`/api/studio/items/${enc(it.id)}/takes/use`, { key: take.key });
      ctx.toast.success(out?.remount?.length ? t('studio.take.usedRemount') : t('studio.take.used'), { timeout: 4000 });
      takesOf.delete(it.id);
      load();
    } catch (err) {
      ctx.toast.error(err);
      btn.disabled = false;
    }
  }

  /** « Refuser cette séquence »: the ad is made without it (remade when it was already assembled). */
  async function refuseSequence(sq) {
    const reason = await ctx.overlay.prompt({
      title: t('studio.refuse.title', { title: sq.title }),
      label: t('studio.refuse.label'), hint: t('studio.refuse.hint'),
      multiline: true, required: false, maxLength: 2000, confirmLabel: t('studio.refuse.confirm'),
    });
    if (reason === null || destroyed) return;
    try {
      await ctx.api.post(`/api/studio/items/${enc(dossier.pub.id)}/refuse`, { sequenceId: sq.id, reason: reason.trim() || undefined });
      ctx.toast.success(t('studio.refuse.done'), { timeout: 4000 });
      backToPub();
      load();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  /** Every version of an image or a sequence, each playable; « Utiliser cette version ». */
  function takesBlock(it) {
    if (!['image', 'sequence'].includes(it.kind)) return null;
    const list = takesOf.get(it.id);
    if (!list) {
      loadTakes(it);
      return h('section', { class: 'sp-section sp-takes' }, h('h4', { class: 'st-section-title' }, t('studio.take.title')), h('div', { class: 'st-loading' }, spinner()));
    }
    const operate = canOperate(dossier.pub) && canOperate(it);
    const cards = [...list].reverse().map((take, i) => {
      const agent = take.agentId ? agentById(take.agentId)?.name : null;
      const media = take.media === 'video'
        ? h('video', { class: 'sp-video', src: take.url, controls: true, preload: 'metadata', playsinline: true })
        : zoomable(take.url, take.name, 'sp-take-img');
      let action = null;
      if (take.inUse) action = h('span', { class: 'sp-take-inuse' }, icon('check', { size: 13 }), t('studio.take.inUse'));
      else if (operate && take.url) {
        const b = button(t('studio.take.use'), { variant: 'secondary', size: 'sm', class: 'sp-take-use' });
        b.addEventListener('click', () => useTake(it, take, b));
        action = b;
      }
      return h('figure', { class: classNames(['sp-take', { 'is-inuse': take.inUse }]) },
        media,
        h('figcaption', { class: 'sp-take-cap' },
          h('span', { class: 'sp-take-n mono-label' }, t('studio.take.n', { n: list.length - i })),
          take.at ? h('time', { class: 'mono-label', title: formatDateTime(take.at) }, relativeTime(take.at)) : null,
          agent ? h('span', { class: 'st-meta' }, agent) : null,
          take.note ? h('p', { class: 'sp-take-note' }, take.note) : null,
          action));
    });
    return h('section', { class: 'sp-section sp-takes' },
      h('h4', { class: 'st-section-title' }, t('studio.take.title'), h('span', { class: 'st-section-count' }, String(list.length))),
      list.length > 1 ? h('p', { class: 'sp-hint' }, t('studio.take.hint')) : null,
      list.length ? h('div', { class: 'sp-takes-grid' }, ...cards) : h('p', { class: 'st-muted' }, t('studio.take.none')));
  }

  /** The page of one element of the ad: « ← Pub 9 », its media, what to do, its versions, its fields, its history. */
  function renderDetail(it) {
    const pub = dossier.pub;
    const type = typeFor(it);
    const media = it.kind === 'image' || it.kind === 'scene' ? 'image' : 'video';
    const inUse = latestFinal(it, media) || mediaFiles(it, media).at(-1) || (it.kind === 'scene' ? captureOf(it) : null);
    const start = startImageOf(it);
    const startFile = start ? latestFinal(start, 'image') || mediaFiles(start, 'image').at(-1) : null;
    const inside = it.kind !== 'sequence' || usedInPub(pub, it);
    const operate = canOperate(it);
    // « ← Pub 9 › S2 · … »: the way back is the first step of the path, nothing else to navigate.
    const back = button(shortTitle(pub.title), { variant: 'secondary', size: 'sm', icon: 'arrowLeft', class: 'sp-back', title: t('studio.pub.backTo', { title: pub.title }), onClick: () => backToPub() });
    back.setAttribute('aria-label', t('studio.pub.backTo', { title: pub.title }));
    const crumb = h('nav', { class: 'sp-crumb', 'aria-label': t('studio.pub.path') },
      back,
      h('span', { class: 'st-crumb-sep', 'aria-hidden': 'true' }, icon('chevronRight', { size: 14 })),
      h('span', { class: 'sp-crumb-current', 'aria-current': 'page' }, it.title));
    const actions = [];
    if (operate && it.kind !== 'scene' && it.id !== dossier.origin?.id) {
      actions.push(button(t('studio.detail.redo'), { variant: 'primary', size: 'sm', icon: 'refresh', onClick: () => regenerate(it) }));
    }
    const sceneEntry = it.kind === 'scene' ? dossier.scenes.find((s) => s.scene.id === it.id) : null;
    const gap = sceneEntry ? sceneGap(sceneEntry, infoOf(it)) : null;
    if (gap?.state === 'skipped' || gap?.state === 'error') {
      const force = forceButton(it, { variant: 'primary' });
      if (force) actions.push(force);
    }
    if (it.kind === 'sequence' && canOperate(pub)) {
      if (inside) actions.push(button(t('studio.detail.refuse'), { variant: 'secondary', size: 'sm', icon: 'close', onClick: () => refuseSequence(it) }));
      else if (it.status !== 'rejected' && pubSequenceIds(pub)) actions.push(button(t('studio.pub.putBack'), { variant: 'secondary', size: 'sm', icon: 'plus', onClick: () => setInPub(it, true) }));
    }
    const reason = isProblem(it) || it.tags?.includes('reserve') || (gap && gap.state !== 'pending') ? waitingReason(it) : '';
    const rows = fieldRows(it);
    el.replaceChildren(...[
      h('div', { class: 'sp-detail-bar' }, crumb),
      h('header', { class: 'sp-head sp-detail-head' },
        h('div', { class: 'sp-head-titles' },
          h('p', { class: 'st-item-kind mono-label' }, type?.label || it.kind),
          h('div', { class: 'st-item-titleline' }, h('h3', { class: 'st-item-title' }, it.title), ref(it)),
          h('div', { class: 'st-item-meta' }, statusMenu(it), reserveBadge(it), it.kind === 'scene' ? gapBadge(it) : null,
            it.kind === 'sequence' && !inside ? h('span', { class: 'sp-inpub is-out' }, t('studio.pub.outOfPub')) : null,
            h('span', { class: 'st-meta mono-label', title: formatDateTime(it.updatedAt) }, t('studio.updatedAgo', { when: relativeTime(it.updatedAt) })))),
        actions.length ? h('div', { class: 'st-actions' }, ...actions) : null),
      reason ? h('p', { class: 'sp-card-reason sp-detail-reason' }, reason) : null,
      h('div', { class: 'sp-detail-grid' },
        h('div', { class: 'sp-detail-media' },
          inUse ? (inUse.media === 'video' ? video(inUse, startFile?.url || null) : zoomable(inUse.url, it.title, 'sp-detail-img')) : missing(it, media),
          startFile ? h('figure', { class: 'sp-detail-start' }, zoomable(startFile.url, start.title, 'sp-start-img'), h('figcaption', { class: 'mono-label' }, t('studio.pub.ourImage'))) : null),
        h('div', { class: 'sp-detail-info' },
          rows.length ? h('dl', { class: 'st-fields' }, ...rows) : h('p', { class: 'st-muted' }, t('studio.noFields')))),
      takesBlock(it),
      h('details', { class: 'sp-section sp-detail-history' },
        h('summary', { class: 'st-section-title' }, t('studio.history'), it.history?.length ? h('span', { class: 'st-section-count' }, String(it.history.length)) : null),
        it.history?.length ? historyList(it, type, agentById, { dated: true }) : h('p', { class: 'st-muted' }, t('studio.pub.noHistory')))].filter(Boolean));
  }

  /** Show the ad (null) or one of its elements, without reloading the page. */
  function showSub(id) {
    if (!sub && id) pubScroll = layer.body?.scrollTop ?? 0;
    const leaving = Boolean(sub) && !id;
    sub = id || null;
    loose = null;
    render();
    if (layer.body) layer.body.scrollTop = leaving ? pubScroll : 0;
  }

  function render() {
    const d = dossier;
    if (!d) return;
    if (sub) {
      const it = findInDossier(sub) || (loose?.id === sub ? loose : null);
      if (it) {
        layer.setTitle?.(`${d.pub.title} · ${it.title}`);
        renderDetail(it);
        return;
      }
      // Not part of this ad (another service's element…): read it on its own, then show it.
      ctx.api.get(`/api/studio/items/${enc(sub)}`).then((x) => {
        if (destroyed || sub !== x.id) return;
        loose = x;
        render();
      }).catch(() => { if (!destroyed) backToPub(); });
      el.replaceChildren(h('div', { class: 'st-loading' }, spinner()));
      return;
    }
    layer.setTitle?.(d.pub.title);
    const competitorCut = d.scenes.some(({ scene }) => captureOf(scene));
    const brief = d.origin?.kind === 'brief' ? d.origin : null;
    if (!brief) { editing = false; editNode = null; missed = false; }
    el.replaceChildren(...[
      crumbs(d.pub),
      header(d.pub),
      h('div', { class: classNames(['sp-top', { 'is-single': !d.origin }]) }, resultBlock(d), brief ? briefCard(brief) : d.origin ? originBlock(d) : null),
      brief ? briefSection(brief) : null,
      competitorCut ? scenesBlock(d) : null,
      oursBlock(d),
      infoBlock(d.pub)].filter(Boolean));
  }

  async function load() {
    const mine = ++seq;
    try {
      const d = await ctx.api.get(`/api/studio/items/${enc(route.id)}/dossier`);
      if (destroyed || mine !== seq) return;
      dossier = d;
      ids = new Set([d.pub.id, d.origin?.id, ...d.scenes.flatMap((s) => [s.scene.id, ...s.images.map((x) => x.id), ...s.sequences.map((x) => x.id)]), ...d.extra.map((x) => x.id)].filter(Boolean));
      const y = layer.body?.scrollTop ?? 0;
      render();
      if (layer.body && y) layer.body.scrollTop = y;
    } catch (err) {
      if (destroyed || mine !== seq) return;
      el.replaceChildren(emptyState({
        icon: 'alert', title: err?.status === 404 ? t('studio.gone') : t('studio.loadError'), text: err?.status === 404 ? '' : err?.message || '',
        action: button(t('studio.back'), { variant: 'secondary', size: 'sm', onClick: () => backToList() }),
      }));
    }
  }

  function backToList() {
    const floorId = dossier?.pub.floorId || store.get().ui.floorId;
    ctx.navigate(floorNumberOf(floors(), floorId) ? studioHash(floors(), floorId, dossier?.pub.deptId || null) : '#/');
  }

  async function changeStatus(pub, sel) {
    sel.disabled = true;
    try {
      await ctx.api.patch(`/api/studio/items/${enc(pub.id)}`, { status: sel.value });
      ctx.toast.success(t('studio.statusChanged', { label: statusInfo(typeFor(pub), sel.value).label }), { timeout: 2500 });
      load();
    } catch (err) {
      ctx.toast.error(err);
      sel.value = pub.status;
    } finally {
      sel.disabled = false;
    }
  }

  async function remove(pub) {
    const ok = await ctx.overlay.confirm({
      title: t('studio.delete.title', { title: pub.title }), message: t('studio.pub.deleteMessage'),
      confirmLabel: t('studio.delete'), danger: true,
    });
    if (!ok || destroyed) return;
    try {
      await ctx.api.del(`/api/studio/items/${enc(pub.id)}`);
      ctx.toast.success(t('studio.deleted'));
      backToList();
    } catch (err) {
      ctx.toast.error(err);
    }
  }

  let pending = null;
  function onEvent(ev) {
    if (!dossier || !ev) return;
    const related = ids.has(ev.id) || (ev.item?.parents || []).some((p) => ids.has(p));
    if (!related) return;
    // « Modifier » open on the brief: a reload would re-render the form under the cursor; it waits
    // until the form closes. A form folded away (section closed by hand) blocks nothing.
    if (editing && editNode && el.contains(editNode) && ev.action !== 'delete') {
      missed = true;
      return;
    }
    if (ev.action === 'delete' && ev.id === dossier.pub.id) {
      el.replaceChildren(emptyState({ icon: 'alert', title: t('studio.gone'), action: button(t('studio.back'), { variant: 'secondary', size: 'sm', onClick: () => backToList() }) }));
      dossier = null;
      return;
    }
    takesOf.delete(ev.id);
    liveReload();
  }

  // A live update never rebuilds the page under a playing video or a running upload: it waits
  // (`waiting`) until the media pauses or ends (caught on the way down: they do not bubble); the
  // end of an upload reloads anyway.
  let waiting = false;
  function liveReload() {
    if (liveReloadWaits(el)) { waiting = true; return; }
    if (pending) return;
    pending = setTimeout(() => {
      timers.delete(pending);
      pending = null;
      if (liveReloadWaits(el)) { waiting = true; return; }
      waiting = false;
      load();
    }, RELOAD_DEBOUNCE_MS);
    timers.add(pending);
  }
  const resume = () => { if (waiting && !destroyed) liveReload(); };
  el.addEventListener('pause', resume, true);
  el.addEventListener('ended', resume, true);

  load();
  return {
    el,
    onEvent,
    showSub,
    reload: () => { takesOf.clear(); return load(); },
    officeChanged: () => render(),
    destroy() {
      destroyed = true;
      for (const id of timers) clearTimeout(id);
      timers.clear();
    },
  };
}
